import type {
    GetInvitationsForOrganizationParams,
    InvitationResponse,
    OrgInvitationRequest,
    PagedResponseInvitationResponse
} from 'generatedTypes/user/models';
import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useQuerystring from 'hooks/useQuerystring';
import useToast from 'hooks/useToast';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE, INVITATION_API } from './constants';

const useInviteUserToOrganization = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { authenticatedDelete, authenticatedGet, authenticatedPost } = useFetch();
    const { broadcastToast } = useToast();
    const {
        DELETE_INVITATION_ERROR_MESSAGE,
        DELETE_INVITATION_SUCCESS_MESSAGE,
        FETCH_INVITATIONS_ERROR_MESSAGE,
        INVITE_USER_TO_ORGANIZATION_ERROR_MESSAGE,
        INVITE_USER_TO_ORGANIZATION_SUCCESS_MESSAGE
    } = useTranslation();

    const { addQuery } = useQuerystring({});

    const deleteInvitation = useCallback(
        async (invitationId: string) => {
            setIsLoading(true);

            try {
                const url = `${INVITATION_API}/${invitationId}`;
                const { error } = await authenticatedDelete(url, null);

                if (error) {
                    throw new Error(error);
                }

                broadcastToast({
                    text: DELETE_INVITATION_SUCCESS_MESSAGE,
                    type: 'success'
                });
            } catch (error) {
                broadcastToast({
                    text: DELETE_INVITATION_ERROR_MESSAGE,
                    type: 'error'
                });
                console.error('deleteInvitation error:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [
            DELETE_INVITATION_ERROR_MESSAGE,
            DELETE_INVITATION_SUCCESS_MESSAGE,
            authenticatedDelete,
            broadcastToast
        ]
    );

    const getInvitations = useCallback(
        async (
            params: GetInvitationsForOrganizationParams
        ): Promise<PagedResponseInvitationResponse> => {
            setIsLoading(true);
            const query = addQuery({
                pageToken: params.pageToken ?? null,
                size: `${params.pageable?.size || DEFAULT_PAGE_SIZE}`
            });

            const { data, error } = await authenticatedGet<PagedResponseInvitationResponse>(
                `${INVITATION_API}?${query}`
            );

            if (error || !data) {
                broadcastToast({
                    text: FETCH_INVITATIONS_ERROR_MESSAGE,
                    type: 'error'
                });
                console.error('getInvitations error:', error);
                setIsLoading(false);
                throw new Error(FETCH_INVITATIONS_ERROR_MESSAGE);
            }

            setIsLoading(false);

            return data;
        },
        [addQuery, authenticatedGet, broadcastToast, FETCH_INVITATIONS_ERROR_MESSAGE]
    );

    const inviteUserToOrganization = useCallback(
        async (values: OrgInvitationRequest) => {
            setIsLoading(true);

            const url = INVITATION_API;
            const requestBody = {
                invitee: values.invitee,
                roles: values.roles
            };

            try {
                const { data, error } = await authenticatedPost<InvitationResponse>(
                    url,
                    JSON.stringify(requestBody)
                );

                if (error) {
                    throw new Error(error);
                }

                broadcastToast({
                    text: INVITE_USER_TO_ORGANIZATION_SUCCESS_MESSAGE,
                    type: 'success'
                });

                return data;
            } catch (error) {
                broadcastToast({
                    text: INVITE_USER_TO_ORGANIZATION_ERROR_MESSAGE,
                    type: 'error'
                });
                console.error('inviteUserToOrganization error:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [
            INVITE_USER_TO_ORGANIZATION_ERROR_MESSAGE,
            INVITE_USER_TO_ORGANIZATION_SUCCESS_MESSAGE,
            authenticatedPost,
            broadcastToast
        ]
    );

    return {
        deleteInvitation,
        getInvitations,
        inviteUserToOrganization,
        isLoading
    };
};

export default useInviteUserToOrganization;
